import { router } from "@inertiajs/react";
import { LoaderIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency } from "@/lib/utils";
import type { IncentivePayout } from "@/types/admin-portal/bonus-ro";
import { type PayoutAction, payoutTransitionUrl } from "./payouts";
import { sendWrite } from "./rules";

/** The text an action cannot be sent without, as the backend field it fills. */
const REQUIRED_TEXT: Partial<Record<PayoutAction, "reference" | "reason">> = {
	paid: "reference",
	cancel: "reason",
};

/** Confirms one status change, asking for the text the action requires. */
export function PayoutActionDialog({
	payout,
	action,
	urlTemplate,
	onClose,
}: {
	payout: IncentivePayout;
	action: PayoutAction;
	urlTemplate: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("bonus-ro");
	const [text, setText] = useState("");
	const [saving, setSaving] = useState(false);

	const field = REQUIRED_TEXT[action];
	const canSave = !saving && (!field || text.trim() !== "");
	const vars = {
		name: payout.therapistName ?? payout.therapistId.slice(0, 8),
		amount: formatCurrency(payout.amount),
	};

	async function onSave() {
		setSaving(true);
		const result = await sendWrite(
			payoutTransitionUrl(urlTemplate, payout.id, action),
			"PUT",
			field ? { [field]: text.trim() } : {},
			t("payout_actions.error"),
		);
		setSaving(false);
		if (!result.ok) {
			toast.error(result.error);
			return;
		}
		toast.success(t(`payout_actions.${action}.done`, vars));
		onClose();
		router.reload({ only: ["payouts"] });
	}

	return (
		<Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t(`payout_actions.${action}.title`, vars)}</DialogTitle>
					<DialogDescription>
						{t(`payout_actions.${action}.confirm`, vars)}
					</DialogDescription>
				</DialogHeader>

				{field && (
					<div className="flex flex-col gap-2">
						<Label htmlFor="bonus-ro-payout-text">
							{t(`payout_actions.${action}.${field}`)}
						</Label>
						{field === "reason" ? (
							<Textarea
								className="bg-input shadow-inner"
								id="bonus-ro-payout-text"
								value={text}
								rows={3}
								autoFocus
								onChange={(e) => setText(e.target.value)}
							/>
						) : (
							<Input
								className="bg-input shadow-inner"
								id="bonus-ro-payout-text"
								value={text}
								autoFocus
								onChange={(e) => setText(e.target.value)}
							/>
						)}
						<p className="text-xs text-muted-foreground">
							{t(`payout_actions.${action}.${field}_hint`)}
						</p>
					</div>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{t("actions.back")}
					</Button>
					<Button
						variant={action === "cancel" ? "destructive" : "default"}
						onClick={onSave}
						disabled={!canSave}
					>
						{saving && <LoaderIcon className="size-4 animate-spin" />}
						{t(`payout_actions.${action}.submit`)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
